import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { formatTRY } from "@/lib/pricing";
import { Truck, Loader2, ShieldCheck, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const captureDesignImage = () => {
  const src = document.querySelector('[data-testid="wardrobe-3d-canvas"] canvas') || document.querySelector("canvas");
  if (!src || !src.width) return null;
  const scale = Math.min(1, 900 / src.width);
  const out = document.createElement("canvas");
  out.width = Math.round(src.width * scale);
  out.height = Math.round(src.height * scale);
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#F5F0E8";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(src, 0, 0, out.width, out.height);
  return out.toDataURL("image/jpeg", 0.85);
};

export function CheckoutDialog({ open, onOpenChange, config, cost, cnc }) {
  const [step, setStep] = useState("shipping"); // shipping | payment
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [ship, setShip] = useState({ fullName: "", phone: "", tckn: "", address: "", city: "" });

  const canShip =
    ship.fullName && ship.phone && ship.tckn.length === 11 && ship.address && ship.city;

  const submit = async () => {
    setLoading(true);
    setPaymentError("");
    try {
      const payload = {
        config,
        costItems: cost.items,
        totalCost: cost.total,
        cnc: {
          sheetCount: cnc.sheetCount,
          firePercent: cnc.firePercent,
          usedAreaM2: cnc.usedAreaM2,
          cutLengthM: cnc.cutLengthM,
          partCount: cnc.partCount,
        },
        shipping: ship,
        previewImage: captureDesignImage(),
      };
      const { data } = await api.post("/payments/iyzico/checkout", payload);
      toast.success("Güvenli ödeme sayfasına yönlendiriliyorsunuz…");
      window.location.assign(data.paymentPageUrl);
    } catch (e) {
      const msg =
        formatApiError(e.response?.data?.detail) || "Ödeme başlatılırken hata oluştu";
      setPaymentError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            {step === "shipping" ? <Truck className="w-5 h-5 text-primary" /> : <ShieldCheck className="w-5 h-5 text-primary" />}
            {step === "shipping" ? "Teslimat Bilgileri" : "Ödeme"}
          </DialogTitle>
          <DialogDescription>
            Toplam tutar: <span className="font-semibold text-primary">{formatTRY(cost.total)}</span>
          </DialogDescription>
        </DialogHeader>

        {step === "shipping" ? (
          <div className="space-y-3">
            <div>
              <Label>Ad Soyad</Label>
              <Input
                data-testid="checkout-fullname-input"
                value={ship.fullName}
                onChange={(e) => setShip({ ...ship, fullName: e.target.value })}
                placeholder="Adınız Soyadınız"
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                data-testid="checkout-phone-input"
                value={ship.phone}
                onChange={(e) => setShip({ ...ship, phone: e.target.value })}
                placeholder="05XX XXX XX XX"
              />
            </div>
            <div>
              <Label>TC Kimlik No</Label>
              <Input
                data-testid="checkout-tckn-input"
                value={ship.tckn}
                onChange={(e) =>
                  setShip({ ...ship, tckn: e.target.value.replace(/\D/g, "").slice(0, 11) })
                }
                placeholder="11 haneli TC Kimlik Numaranız"
                inputMode="numeric"
                maxLength={11}
              />
              <p className="text-[11px] text-stone-400 mt-1">
                Ödeme güvenliği (iyzico) için gereklidir.
              </p>
            </div>
            <div>
              <Label>Adres</Label>
              <Input
                data-testid="checkout-address-input"
                value={ship.address}
                onChange={(e) => setShip({ ...ship, address: e.target.value })}
                placeholder="Mahalle, sokak, no, daire"
              />
            </div>
            <div>
              <Label>Şehir</Label>
              <Input
                data-testid="checkout-city-input"
                value={ship.city}
                onChange={(e) => setShip({ ...ship, city: e.target.value })}
                placeholder="İstanbul"
              />
            </div>
            <Button
              data-testid="checkout-to-payment-btn"
              className="w-full rounded-full mt-2"
              disabled={!canShip}
              onClick={() => setStep("payment")}
            >
              Ödemeye Geç
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div
              className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs p-2.5 flex items-center gap-2"
              data-testid="checkout-secure-info"
            >
              <Lock className="w-3.5 h-3.5 shrink-0" />
              Ödeme, iyzico'nun güvenli ödeme sayfasında yapılır. Kart bilgileriniz sitemize hiç uğramaz.
            </div>
            <div className="rounded-xl border border-[#F0EAE2] p-3 text-sm space-y-1.5">
              <div className="flex justify-between">
                <span className="text-stone-500">Alıcı</span>
                <span className="font-medium">{ship.fullName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Teslimat</span>
                <span className="font-medium">{ship.city}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Tutar</span>
                <span className="font-semibold text-primary">{formatTRY(cost.total)}</span>
              </div>
            </div>
            {paymentError && (
              <div
                className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs p-2.5 flex items-center gap-2"
                data-testid="checkout-payment-error"
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {paymentError}
              </div>
            )}
            <Button
              data-testid="checkout-pay-button"
              className="w-full rounded-full mt-2"
              disabled={loading}
              onClick={submit}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
              {formatTRY(cost.total)} — iyzico ile Güvenli Öde
            </Button>
            <button
              className="w-full text-xs text-stone-500 hover:text-stone-700"
              onClick={() => setStep("shipping")}
            >
              ← Teslimat bilgilerine dön
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
